/**
 * Step 7 · Draft review (ux-flows §2 step 7, design-spec/07 plate 06).
 *
 * His words, his letter. Three things on one screen, per the signed-off mock:
 *   (a) the drafted letter in the canonical serif letter card — EDITABLE IN PLACE,
 *   (b) tone presets + the regeneration counter,
 *   (c) the verbatim transcript, so every line traces back to something he said.
 *
 * **Inline editing is the primary affordance** and the floor beneath everything else: if the draft
 * is weak, or a regeneration is refused, or the cap is spent, the referee can still write it
 * themselves — the favour is never wasted. Edits are FREE and uncapped server-side and never spend
 * the regeneration budget, so nothing here may disable the textarea.
 *
 * **Layout.** Two dense panels on a phone is the named risk, so the transcript is a collapsed
 * accordion beneath the letter on mobile and only sits beside it at ≥1024px, where the shell
 * widens for this one step. Tablets get the accordion too: 768px is not enough for two panels of
 * prose without both becoming unreadable.
 *
 * **The textarea is the source of truth for the letter while focused, and the server is the source
 * of truth for the counter.** Never the reverse: the cap is derived from the version chain
 * server-side and re-checked under a row lock, so this screen only ever *reports* what the last
 * response said. See `onRegenerate` for the two 409s that must read differently.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import {
  DRAFT_ERROR_CAP_REACHED,
  RefereeApiError,
  type DraftState,
  type DraftWrite,
  type LetterTone,
} from '@/lib/api-types';
import { refereeDraftQueryKey } from '@/lib/draft-query';
import { cn } from '@/lib/utils';
import { refereeContextQueryKey, useSession } from '@/session/useSession';

/**
 * The tone chips. LABEL is the founder-signed-off mock's copy; TONE is the backend's `LetterTone`
 * vocabulary verbatim (an unknown value is a 400 `invalid_tone`).
 *
 * The mock also shows a "Redo from my recording ›" link. It is deliberately NOT built: there is no
 * wire verb for it. Every regeneration already re-drafts from the key points extracted from the
 * recording, and `tone` is required — so a "Redo" control could only send one of these three
 * tones, making it an unlabelled duplicate of a chip that also silently spends a paid call.
 * Flagged for the founder rather than faked.
 */
const TONE_CHIPS: ReadonlyArray<{ tone: LetterTone; label: string }> = [
  { tone: 'warm', label: 'Warmer' },
  { tone: 'professional', label: 'More formal' },
  { tone: 'concise', label: 'Shorter' },
];

/**
 * The errors whose only correct handling is leaving (Sign has the same twin): `conflict` (the
 * request has left `drafted`) and `unauthorized` (an unhealable 401 — the same situation wearing
 * the session's clothes). While the guard relocates, "try tapping away again" is advice that
 * cannot work, so the save-failure banner must not show for these.
 */
function isLeaveKind(error: unknown): boolean {
  return error instanceof RefereeApiError && (error.kind === 'conflict' || error.kind === 'unauthorized');
}

export function Review() {
  const { token, context } = useSession();
  const queryClient = useQueryClient();

  const draft = useQuery({
    queryKey: refereeDraftQueryKey(token),
    queryFn: () => apiClient.pollDraft(token),
    enabled: token.length > 0,
    staleTime: 15_000,
  });

  /**
   * The letter the referee is editing. Local, because a controlled value fed straight from the
   * query would fight their typing on every background refetch. It re-syncs only when the SERVER's
   * letter actually changes (a regeneration, or another tab's edit) — tracked by comparing the
   * last body we synced from, so re-syncing never silently discards what they are mid-way through.
   */
  const [body, setBody] = useState('');
  const syncedFrom = useRef<string | null>(null);
  const serverBody = draft.data?.letterBody;

  useEffect(() => {
    if (serverBody !== undefined && serverBody !== syncedFrom.current) {
      syncedFrom.current = serverBody;
      setBody(serverBody);
    }
  }, [serverBody]);

  /** The counter, always the server's. `undefined` until the first poll answers. */
  const remaining = draft.data?.regenerationsRemaining;
  const [capSpent, setCapSpent] = useState(false);
  /** Set when the letter can no longer change at all (the request has left `drafted`). */
  const [frozen, setFrozen] = useState(false);

  /** Fold a write echo back into the draft cache so the counter + letter stay the server's. */
  const applyWrite = useCallback(
    (write: DraftWrite) => {
      queryClient.setQueryData(refereeDraftQueryKey(token), (prev: DraftState | undefined) =>
        prev
          ? {
              ...prev,
              letterBody: write.letterBody,
              version: write.version,
              regenerationsUsed: write.regenerationsUsed,
              regenerationsRemaining: write.regenerationsRemaining,
            }
          : prev,
      );
    },
    [queryClient, token],
  );

  /**
   * This screen's view of the world is stale — re-read the context and let the step guard move
   * the referee to where they now actually are. Also the whole handling for a surfaced
   * `unauthorized`: the transport has already tried the designed recovery (re-accept → retry), so
   * one reaching here means the request left the resumable states, and the context is the only
   * authority on where it went.
   */
  const leaveForTruth = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: refereeContextQueryKey(token) });
  }, [queryClient, token]);

  /**
   * A write refused because the request has left `drafted` (`cannot_edit` / `cannot_regenerate`)
   * means the letter is frozen — most likely signed in another tab. Say so, and leave for truth.
   */
  const handleFrozen = useCallback(() => {
    setFrozen(true);
    leaveForTruth();
  }, [leaveForTruth]);

  // A poll that fails `unauthorized` must not strand the referee on a skeleton forever — there is
  // no letter to render and no retry that can help, so the guard is the only way somewhere true.
  const draftError = draft.error;
  useEffect(() => {
    if (draftError instanceof RefereeApiError && draftError.kind === 'unauthorized') leaveForTruth();
  }, [draftError, leaveForTruth]);

  const edit = useMutation({
    mutationFn: (letterBody: string) => apiClient.editDraft(token, { letterBody }),
    onSuccess: (write) => {
      syncedFrom.current = write.letterBody;
      applyWrite(write);
    },
    onError: (error) => {
      if (!(error instanceof RefereeApiError)) return;
      if (error.kind === 'conflict') handleFrozen();
      // No frozen banner for `unauthorized`: "already signed" is only the likeliest truth, not a
      // known one (withdrawn/expired surface the same way). The guard will say which it was.
      else if (error.kind === 'unauthorized') leaveForTruth();
    },
  });

  const regenerate = useMutation({
    mutationFn: (tone: LetterTone) => apiClient.regenerateDraft(token, { tone }),
    onSuccess: (write) => {
      syncedFrom.current = write.letterBody;
      setBody(write.letterBody);
      applyWrite(write);
    },
    onError: (error) => {
      if (!(error instanceof RefereeApiError)) return;
      // Two different 409s that must NOT read the same:
      //  - the cap is spent → the button is dead, but their own words still are not (edit lives on)
      //  - the request left `drafted` → the letter is frozen; there is nothing to do here but go
      if (error.code === DRAFT_ERROR_CAP_REACHED) setCapSpent(true);
      else if (error.kind === 'conflict') handleFrozen();
      else if (error.kind === 'unauthorized') leaveForTruth();
    },
  });

  /** Save on blur. An unchanged body appends nothing server-side, so this is cheap to be liberal with. */
  const onBlur = () => {
    const next = body.trim();
    if (!next || next === syncedFrom.current || frozen) return;
    edit.mutate(next);
  };

  const outOfRegenerations = capSpent || remaining === 0;
  const letterReady = draft.data?.status === 'drafted' && serverBody !== undefined;
  const meta = [context?.referee.relationship && RELATIONSHIP_LABELS[context.referee.relationship], context?.referee.company]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink">
          Your letter
        </h1>
        {/* The promise, kept visible: no seal, no signature, nothing sent. Gold is the sign moment
            only, so this chip is deliberately neutral. */}
        <span className="inline-flex flex-none items-center rounded-pill bg-paper-2 px-2.5 py-1 font-display text-[10px] font-semibold text-ink-70">
          Draft — not sent
        </span>
      </div>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        This is a draft from your words. Change anything — it&rsquo;s your reference.
      </p>

      {/* Beside the draft only from 1024px up; stacked everywhere below it. */}
      <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        <div className="flex flex-col lg:flex-1">
          {letterReady ? (
            <LetterEditor
              body={body}
              meta={meta}
              onChange={setBody}
              onBlur={onBlur}
              saving={edit.isPending}
              regenerating={regenerate.isPending}
            />
          ) : (
            <LetterSkeleton />
          )}

          <ToneRow
            disabled={!letterReady || frozen || regenerate.isPending}
            outOfRegenerations={outOfRegenerations}
            remaining={remaining}
            pendingTone={regenerate.isPending ? regenerate.variables : undefined}
            onPick={(tone) => regenerate.mutate(tone)}
          />
        </div>

        {/* Traceability is a compliance mitigation, not a nicety — but it must not bury the letter
            on a phone, so below 1024px it is a collapsed accordion. */}
        <Transcript transcript={draft.data?.transcript} className="lg:w-[320px] lg:flex-none" />
      </div>

      <p className="mt-3 text-center font-body text-[11px] leading-[1.45] text-ink-70">
        AI prepared this draft based solely on your recorded answers.
      </p>

      <div className="mt-auto flex flex-col gap-2.5 pt-6">
        {frozen ? (
          <p role="alert" className="text-center text-[12px] font-medium leading-relaxed text-coral-text">
            This letter can&rsquo;t be changed any more — it looks like it has already been signed.
          </p>
        ) : letterReady ? (
          <Button variant="coral" size="block" asChild>
            <Link to={`/r/${token}/sign`}>Continue to signing</Link>
          </Button>
        ) : (
          // A real <button disabled> until the letter is on screen — never a "disabled" <Link>.
          // With `asChild`, `disabled` lands on the <a>, where browsers ignore it and the cva
          // `disabled:` styles (`:disabled` pseudo-class) never match — a fully clickable link
          // styled as enabled. The step model unlocks `sign` at `drafted`, so this control is the
          // ONLY thing stopping a referee on a slow connection from signing a letter they have
          // not yet seen.
          <Button variant="coral" size="block" disabled>
            Continue to signing
          </Button>
        )}
        {edit.isError && !frozen && !isLeaveKind(edit.error) && (
          <p role="alert" className="text-center text-[12px] font-medium text-coral-text">
            We couldn&rsquo;t save that change — it&rsquo;s still here, try tapping away again.
          </p>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * Letter
 * ---------------------------------------------------------------------------------------------- */

const RELATIONSHIP_LABELS: Record<string, string> = {
  manager: 'Manager',
  colleague: 'Colleague',
  client: 'Client',
  mentor: 'Mentor',
  direct_report: 'Direct report',
  other: 'Reference',
};

/**
 * The canonical letter card, editable in place.
 *
 * A `<textarea>` styled as the letter rather than a contenteditable: it is the same "tap the words
 * and type" gesture, but it announces properly, keeps its value out of `innerHTML` (a
 * contenteditable would happily accept pasted markup into text that ends up in a signed PDF), and
 * cannot drift into rich-text state. Auto-grown so the whole letter is always visible — a letter
 * you have to scroll inside is not a letter you can read before you put your name to it.
 */
function LetterEditor({
  body,
  meta,
  onChange,
  onBlur,
  saving,
  regenerating,
}: {
  body: string;
  meta: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  saving: boolean;
  regenerating: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Grow to fit the content (jsdom reports scrollHeight 0, which simply leaves the min-height).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [body]);

  return (
    <div className="relative rounded-lg border border-line bg-surface p-4 shadow-sm">
      {meta && (
        <div className="mb-2 font-display text-[9.5px] font-semibold uppercase tracking-[0.04em] text-ink-70">
          {meta}
        </div>
      )}

      <label htmlFor="letter-body" className="sr-only">
        Your letter — edit any word
      </label>
      <textarea
        id="letter-body"
        ref={ref}
        value={body}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        spellCheck
        // AAA on referee body text: 15px serif at 1.62 on surface, ink on paper.
        className={cn(
          'block min-h-[180px] w-full resize-none border-0 bg-transparent p-0 font-serif text-[15px] leading-[1.62] text-ink',
          'focus:outline-none focus-visible:outline-none',
          regenerating && 'opacity-40 transition-opacity duration-ui',
        )}
      />

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="font-display text-[10px] font-medium text-ink-70">
          {saving ? 'Saving…' : 'Tap the letter to edit any word'}
        </span>
      </div>

      {/* Sets up the ceremony without pre-empting it — no seal, no signature yet. */}
      <div className="mt-2.5 rounded-md border border-dashed border-line-2 p-2 text-center font-display text-[10px] font-medium text-ink-70">
        Your signature — added when you sign
      </div>
    </div>
  );
}

/** The letter's shape while the first poll answers — never a bare spinner. */
function LetterSkeleton() {
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm" aria-hidden="true">
      <div className="flex flex-col gap-2.5">
        {['w-[38%]', 'w-full', 'w-[94%]', 'w-[88%]', 'w-[60%]'].map((w) => (
          <div key={w} className={cn('skel h-[11px] rounded-[6px]', w)} />
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * Tone presets + the cap
 * ---------------------------------------------------------------------------------------------- */

function ToneRow({
  disabled,
  outOfRegenerations,
  remaining,
  pendingTone,
  onPick,
}: {
  disabled: boolean;
  outOfRegenerations: boolean;
  remaining: number | undefined;
  pendingTone: LetterTone | undefined;
  onPick: (tone: LetterTone) => void;
}) {
  return (
    <div className="mt-3">
      <div className="mb-1.5 font-display text-[9px] font-semibold uppercase tracking-[0.12em] text-ink-70">
        Quick adjustments
      </div>

      <div role="group" aria-label="Rewrite the letter in a different tone" className="flex flex-wrap gap-2">
        {TONE_CHIPS.map(({ tone, label }) => (
          <button
            key={tone}
            type="button"
            disabled={disabled || outOfRegenerations}
            onClick={() => onPick(tone)}
            className={cn(
              'inline-flex min-h-[44px] items-center gap-1.5 rounded-pill border px-4 font-display text-[13px] font-medium',
              'transition-colors duration-ui ease-thread',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
              'border-line-2 bg-surface text-ink-70 hover:border-coral/40 hover:text-ink',
              'disabled:pointer-events-none disabled:opacity-40',
            )}
          >
            {pendingTone === tone && <SpinnerIcon />}
            {label}
          </button>
        ))}
      </div>

      {/* The cap, plainly — "so the referee can see the limit coming rather than discover it by
          being refused". `aria-live` because it also lands as the RESULT of a refused press. */}
      <p className="mt-2 font-body text-[10.5px] leading-relaxed text-ink-70" aria-live="polite">
        {outOfRegenerations ? (
          // Distinctly NOT "the letter can't change": the button is spent, their words are not.
          <span className="font-medium text-ink-70">
            That&rsquo;s all three rewrites used — you can still edit every word yourself.
          </span>
        ) : remaining === undefined ? (
          ''
        ) : (
          `${remaining} ${remaining === 1 ? 'redraft' : 'redrafts'} left`
        )}
      </p>
    </div>
  );
}

function SpinnerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5 animate-spin" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={2.5} opacity={0.25} />
      <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" />
    </svg>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * Transcript
 * ---------------------------------------------------------------------------------------------- */

/**
 * "What you said" — the evidence trail.
 *
 * The wire carries the transcript as ONE string with no timestamps and no speaker turns, so the
 * mock's "0:04" gutter is not rendered: a fabricated timestamp against a real quote is exactly the
 * kind of invented detail this product exists not to produce. Paragraphs are split on blank lines,
 * which is what the transcription provider's output actually contains.
 *
 * `<details>` rather than a JS accordion: it is open-by-default at ≥1024px (where it sits beside
 * the letter as a panel) via the `open` attribute being irrelevant to a panel that is always shown,
 * and it is keyboard- and screen-reader-native everywhere else for free.
 */
function Transcript({ transcript, className }: { transcript: string | undefined; className?: string }) {
  if (!transcript) return null;
  const paragraphs = transcript.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  return (
    <details className={cn('group rounded-md border border-line bg-surface lg:open:pb-0', className)}>
      <summary
        className={cn(
          'flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5',
          'font-display text-[11.5px] font-semibold text-ink',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25',
          '[&::-webkit-details-marker]:hidden',
        )}
      >
        What you said — the transcript
        {/* ink-52 (GLYPH tier, 3.33:1) rather than the mock's decorative ink-38: this chevron is
            the only thing announcing open/closed, so 1.4.11's 3:1 floor applies to it. */}
        <ChevronIcon className="h-3.5 w-3.5 flex-none text-ink-52 transition-transform duration-ui group-open:rotate-180" />
      </summary>
      <div className="border-t border-line px-3 pb-3 pt-1">
        {paragraphs.map((p, i) => (
          <p key={i} className="mt-2 font-body text-[11.5px] leading-[1.5] text-ink-70">
            {p}
          </p>
        ))}
      </div>
    </details>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
